import React from 'react';
import { Box, Container, Typography, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { motion } from 'framer-motion';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import UpdateIcon from '@mui/icons-material/Update';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StorageIcon from '@mui/icons-material/Storage';

const features = [
  {
    icon: <CloudSyncIcon sx={{ fontSize: 40 }} />,
    title: 'Data Ingestion',
    description: 'Seamlessly integrate with modern databases, REST APIs, and custom data sources with real-time synchronization and intelligent conflict resolution.',
  },
  {
    icon: <SearchIcon sx={{ fontSize: 40 }} />,
    title: 'Advanced Search',
    description: 'Find critical information instantly with our intelligent search system featuring full-text search, category filtering, and relevance ranking.',
  },
  {
    icon: <LockIcon sx={{ fontSize: 40 }} />,
    title: 'Role-Based Access',
    description: 'Secure, hierarchical user permissions ensure that sensitive information is only accessible to authorized personnel.',
  },
  {
    icon: <UpdateIcon sx={{ fontSize: 40 }} />,
    title: 'Real-Time Updates',
    description: 'Live data synchronization keeps all users informed with the latest intelligence as it becomes available.',
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
    title: 'CJIS Compliance',
    description: 'Built to meet CJIS v6.0 requirements with FBI-approved security standards and comprehensive audit trails.',
  },
  {
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
    title: 'Scalable Architecture',
    description: 'Cloud-native infrastructure that grows with your needs, from small departments to large agencies.',
  },
];

const FeaturesSection: React.FC = () => {
  return (
    <Box
      id="features"
      sx={{
        py: 10,
        backgroundColor: '#000000',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.25rem' },
            }}
          >
            Core Features
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 6,
              color: '#9CA3AF',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Everything you need to manage intelligence data effectively and securely
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#0A0A0A',
                    border: '1px solid #1A1A1A',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#1D9BF0',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(29, 155, 240, 0.2)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        color: '#1D9BF0',
                        mb: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: '#E7E9EA',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#C7CDD3',
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;